import clsx from 'clsx';
import { PropsWithChildren, ReactNode, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeasure } from 'react-use';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Content,
  FormatContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useMenuState } from 'src/contexts/MenuContext/MenuContext';
import { SwitchAccountProvider } from 'src/contexts/SwitchAccountContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useFetchLoginContext } from 'src/hooks/useFetchLoginContext';
import { useIsNativeApp } from 'src/hooks/useIsNativeApp';
import { useMainAccessibleRoute } from 'src/hooks/useMainAccessibleRoute';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useNetworkState } from 'src/hooks/useNetworkState';
import { useRefreshListingOnNotification } from 'src/hooks/useRefreshListingOnNotification';
import { MainNavTopBar } from 'src/navigations/MainNav/MainNavTopBar';
import { Offline } from 'src/navigations/Routes/Offline';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { navigateToMainRoute } from 'src/utils/deepLinkUtils';
import { formatPercent } from 'src/utils/numberFormatter';
import { hasAccessToRoute } from 'src/utils/userUtils';

import { MainNavSideBar, MobileTopBar } from '../MainNav';
import { MainRoute } from '../Routes/MainRoute';
import { HelmetContent } from './HelmetContent';
import * as styles from './LayoutContent.css';
import {
  BottomNavBarContainer,
  UnauthorizedAccessMessageContainer,
} from './LayoutContent.styled';
import { LayoutContentHeader } from './LayoutContentHeader';

const OFFLINE_TIMEOUT_MS = 15 * 1000;

export const LayoutContent = ({
  children,
  mainRoute,
  routeTitle,
  centerContent,
  rightContent,
  returnUrl,
}: PropsWithChildren<{
  mainRoute: MainRoute;
  routeTitle?: string;
  centerContent?: ReactNode;
  rightContent?: ReactNode;
  returnUrl?: string;
}>) => {
  const { appContext, loginContext, setLoginContext, setMainRoute } =
    useAppContext();
  const { isLoading } = useContentContext();
  const { isMenuOpened, toggleIsMenuOpened } = useMenuState();
  const isOnline = useNetworkState(OFFLINE_TIMEOUT_MS);
  const navigate = useNavigate();
  const { showErrorDialog } = useClientLogContext();
  const { fetchLoginContext } = useFetchLoginContext();
  const isMobile = useMatchMedia('mobile');
  const [bottomNavbarRef, { height: bottomNavbarHeight }] =
    useMeasure<HTMLDivElement>();
  useRefreshListingOnNotification();
  const { isOnSidePanel, onCancelSession, isOnCompanionEvent } =
    useEventHubContext();

  const isNativeApp = useIsNativeApp();

  const { performMemoryCleanup, memoryOverThresholdExceeded } =
    useAppPerformanceContext();

  // Set what view we're in
  useEffect(() => {
    setMainRoute(mainRoute);
    if (
      // Only get login context if the route is not one of the login/logout ones
      ![
        MainRoute.Login,
        MainRoute.LoginCallback,
        MainRoute.LoginFailure,
        MainRoute.Logout,
      ].includes(mainRoute)
    ) {
      fetchLoginContext(setLoginContext, (error) => {
        showErrorDialog('LoginClient.getLoginContext', error, {
          onDismissError: () => {
            navigate(`/login`, {
              state: { returnUrl: window.location.pathname },
            });
          },
          trackErrorData: {
            returnUrl: window.location.pathname,
            mainRoute,
            loginContext,
          },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAccess = hasAccessToRoute(
    loginContext?.user,
    appContext?.features,
    mainRoute
  );

  const mainRouteAccess = useMainAccessibleRoute();

  const shouldShowMobileTopBar = useMemo(() => {
    if (isNativeApp) {
      return false;
    }
    if (!isMobile) {
      return false;
    }
    if (mainRoute === MainRoute.InventoryEvent) {
      return false;
    }
    return true;
  }, [isMobile, isNativeApp, mainRoute]);

  const inCompanionMode = useMemo(() => {
    return isOnSidePanel || isOnCompanionEvent;
  }, [isOnCompanionEvent, isOnSidePanel]);

  if (!hasAccess) {
    if (loginContext?.user) {
      // If the user doesn't have access
      // Find the first route he has access to and direct him to that
      if (mainRouteAccess) {
        navigateToMainRoute(mainRouteAccess, navigate);
      }
    } else {
      // If the user is null, the login may not complete so just wait
      // until the login user is not null
      return isOnline ? (
        <div className={styles.spinnerContainer}>
          <PosSpinner />
        </div>
      ) : (
        <Offline />
      );
    }
  }

  return (
    <div
      className={clsx(styles.root, {
        [styles.companionModeContainer]: inCompanionMode,
      })}
    >
      {inCompanionMode && (
        <Button
          className={styles.exitCompanionModeButton}
          onClick={onCancelSession}
        >
          <Content id={ContentId.ExitCompanionMode} />
          <CrossIcon size={vars.iconSize.m} fill={vars.color.textInvertedV2} />
        </Button>
      )}

      <SwitchAccountProvider>
        {(!isMobile || isMenuOpened) && !isOnSidePanel && !isNativeApp && (
          <MainNavSideBar
            toggleMenu={toggleIsMenuOpened}
            isExpanded={isMenuOpened}
          />
        )}
      </SwitchAccountProvider>

      <main className={styles.content}>
        {(appContext?.version ?? '') < (loginContext?.version ?? '') && (
          // Using < so in case the client has higher version than the server
          // (when deploy happens client bits from SVC2 may be hitting api from SVC3, should be very rare)
          // To test this locally, change the < to <= to see the message/button
          <Stack
            gap="l"
            width="full"
            justifyContent="center"
            className={styles.refreshNowContainer}
          >
            <WarningMessage
              message={<Content id={ContentId.NewAppVersion} />}
            />
            <Button onClick={() => window.location.reload()}>
              <Content id={ContentId.RefreshNow} />
            </Button>
          </Stack>
        )}
        {memoryOverThresholdExceeded && (
          // Using < so in case the client has higher version than the server
          // (when deploy happens client bits from SVC2 may be hitting api from SVC3, should be very rare)
          // To test this locally, change the < to <= to see the message/button
          <Stack
            gap="l"
            width="full"
            justifyContent="center"
            className={styles.refreshNowContainer}
          >
            <WarningMessage
              message={
                <FormatContent
                  id={FormatContentId.MemoryUsageExceeded}
                  params={[`${formatPercent(memoryOverThresholdExceeded[0])}`]}
                />
              }
            />
            <Button onClick={performMemoryCleanup}>
              <Content id={ContentId.RecoverMemory} />
            </Button>
          </Stack>
        )}
        <HelmetContent routeTitle={routeTitle} />
        {isLoading ? (
          isOnline ? (
            <div className={styles.spinnerContainer}>
              <PosSpinner />
            </div>
          ) : (
            <Offline />
          )
        ) : hasAccess ? (
          <>
            {shouldShowMobileTopBar && (
              <MobileTopBar toggleMenu={() => toggleIsMenuOpened(true)} />
            )}
            {!isMobile &&
              [
                MainRoute.Home,
                MainRoute.Inventory,
                MainRoute.InventoryEvent,
                MainRoute.InventorySearch,
                MainRoute.InventorySearchV2,
                MainRoute.Sales,
                MainRoute.SalesEvent,
                MainRoute.SalesSearch,
                MainRoute.SalesSearchV2,
                MainRoute.Purchases,
                MainRoute.PurchasesEvent,
                MainRoute.PurchaseSearch,
                MainRoute.PurchaseSearchV2,
                MainRoute.Payments,
                MainRoute.Reports,
                MainRoute.ReportsV2,
                MainRoute.Customers,
                MainRoute.SponsoredListings,
                MainRoute.Settings,
                MainRoute.Discovery,
                MainRoute.HelpCenter,
                MainRoute.Seasons,
              ].includes(mainRoute) &&
              routeTitle && (
                <LayoutContentHeader
                  routeTitle={routeTitle}
                  centerContent={centerContent}
                  rightContent={rightContent}
                  returnUrl={returnUrl}
                />
              )}
            {children}
          </>
        ) : (
          loginContext?.user && (
            <UnauthorizedAccessMessageContainer>
              <Content id={ContentId.AccessToViewUnauthorized} />
            </UnauthorizedAccessMessageContainer>
          )
        )}
        {isMobile && !isNativeApp && (
          <>
            <div
              style={{
                minHeight: `${bottomNavbarHeight}px`,
                maxHeight: `${bottomNavbarHeight}px`,
              }}
            />
            <BottomNavBarContainer ref={bottomNavbarRef}>
              <MainNavTopBar />
            </BottomNavBarContainer>
          </>
        )}
      </main>
    </div>
  );
};
